import { useRef, useState } from 'react';
import {
  Flex,
  useDisclosure,
  Icon,
  Spinner,
  type FlexProps,
} from '@chakra-ui/react';
import {
  Download01,
  Eye,
  File04,
  RefreshCw01,
  XClose,
} from '@kula-ai/icons-core';
import { Replace } from '@/assets/icons/ats';
import { useShowToast } from '@/hooks';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import { IconButton } from '../IconButton';
import { PreviewPdfModal } from './PreviewPdfModal';
import { canPreview } from './previewSupport';

type FileLike =
  | File
  | {
      url?: string;
      file_name?: string;
      filename?: string;
      name?: string;
      content_type?: string;
      type?: string;
      preview?: { url?: string } | null;
    };

interface ViewFileProps {
  file: FileLike;
  showDelete?: boolean;
  handleDelete?: () => void;
  primaryColor?: string;
  showDownload?: boolean;
  showPreview?: boolean;
  showReplace?: boolean;
  onRefresh?: () => void;
  handleUpload?: (_files: File[]) => void;
  acceptedFiles?: string | string[];
  showIcon?: boolean;
  hideTextReference?: boolean;
  containerProps?: FlexProps;
  textContainerProps?: FlexProps;
  useDirect?: boolean;
  isTransferred?: boolean;
  transferInfo?: unknown;
  maxW?: string;
}

const readField = (
  file: FileLike,
  key: 'file_name' | 'filename' | 'name' | 'url'
): string => {
  if (file instanceof File) {
    if (key === 'name' || key === 'file_name' || key === 'filename')
      return file.name;
    return '';
  }
  return (file?.[key] as string) || '';
};

export const ViewFile = ({
  file,
  showDelete = false,
  handleDelete = () => {},
  primaryColor = '',
  showDownload = true,
  showPreview = true,
  showReplace = false,
  onRefresh,
  handleUpload = () => {},
  acceptedFiles = '',
  showIcon = false,
  hideTextReference = false,
  containerProps = {},
  textContainerProps = {},
  useDirect = false,
  isTransferred = false,
  transferInfo = null,
  maxW = '380px',
}: ViewFileProps) => {
  const file_name = readField(file, 'file_name');
  const filename = readField(file, 'filename');
  const name = readField(file, 'name');
  const url = readField(file, 'url');
  const toast = useShowToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const attachFileRef = useRef<HTMLInputElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = () => {
    setIsDownloading(true);
    const cleanup = () => setIsDownloading(false);

    downloadFileFromUrl({
      filename: file_name || filename || name,
      url: file instanceof File ? URL.createObjectURL(file) : url,
      useDirect,
      onSuccess: () => {
        toast({ title: `File downloaded` });
        cleanup();
      },
      onError: (_error: unknown) => {
        toast({ title: 'Failed to download', status: 'error' });
        cleanup();
      },
    });
  };

  const acceptValue = Array.isArray(acceptedFiles)
    ? acceptedFiles.join(', ')
    : acceptedFiles;

  return (
    <>
      <Flex
        border="1px solid"
        borderRadius="6px"
        borderColor="border-color"
        p="12px 16px"
        justifyContent="space-between"
        alignItems="center"
        gap="12px"
        {...containerProps}
      >
        <Flex
          alignItems="center"
          gap="12px"
          flex={1}
          minW={0}
          {...textContainerProps}
        >
          {showIcon && (
            <Flex
              bg="secondary-50"
              p="8px"
              borderRadius="8px"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={File04} boxSize="24px" />
            </Flex>
          )}
          <TruncatedTextWithTooltip
            size="sm-medium"
            maxW={maxW}
            text={file_name || filename || name}
            enableTooltip={true}
          />
          {isTransferred && transferInfo ? (
            <TransferredBadge transferInfo={transferInfo} />
          ) : null}
        </Flex>
        <Flex gap="12px" flexShrink={0}>
          {onRefresh && (
            <IconButton
              text={!hideTextReference ? 'Refresh' : ''}
              icon={RefreshCw01}
              iconContainerProps={{
                width: 'unset',
                height: 'unset',
                bg: 'unset',
              }}
              iconProps={{
                width: '18px',
                height: '18px',
                color: primaryColor ? primaryColor : 'ats-primary',
              }}
              gap="4px"
              onClick={onRefresh}
              type="button"
              textProps={{
                color: primaryColor ? primaryColor : 'ats-primary',
              }}
            />
          )}
          {canPreview(file) && showPreview && (
            <IconButton
              text={!hideTextReference ? 'Preview' : ''}
              icon={Eye}
              iconContainerProps={{
                width: 'unset',
                height: 'unset',
                bg: 'unset',
              }}
              iconProps={{
                width: '18px',
                height: '18px',
                color: primaryColor ? primaryColor : 'ats-primary',
              }}
              gap="4px"
              onClick={onOpen}
              type="button"
              textProps={{
                color: primaryColor ? primaryColor : 'ats-primary',
              }}
            />
          )}
          {showDownload && (
            <IconButton
              text={!hideTextReference && !isDownloading ? 'Download' : ''}
              icon={isDownloading ? Spinner : Download01}
              iconContainerProps={{
                width: 'unset',
                height: 'unset',
                bg: 'unset',
              }}
              iconProps={{
                width: '18px',
                height: '18px',
                color: primaryColor ? primaryColor : 'ats-primary',
              }}
              gap="4px"
              onClick={handleDownload}
              type="button"
              disabled={isDownloading}
              textProps={{ color: primaryColor ? primaryColor : 'ats-primary' }}
            />
          )}
          {showReplace && (
            <Flex onClick={() => attachFileRef?.current?.click()}>
              <input
                ref={attachFileRef}
                type="file"
                accept={acceptValue}
                onChange={(event) => {
                  if (event?.target?.files?.length) {
                    handleUpload(Array.from(event.target.files));
                  }
                }}
                max={1}
                style={{ visibility: 'hidden', height: 0, width: 0 }}
              />
              <IconButton
                text="Replace"
                icon={Replace}
                iconContainerProps={{
                  width: 'unset',
                  height: 'unset',
                  bg: 'unset',
                }}
                iconProps={{
                  width: '18px',
                  height: '18px',
                  color: primaryColor ? primaryColor : 'ats-primary',
                }}
                gap="4px"
                type="button"
                textProps={{
                  color: primaryColor ? primaryColor : 'ats-primary',
                }}
              />
            </Flex>
          )}
          {showDelete && (
            <IconButton
              text={!hideTextReference ? 'Delete' : ''}
              icon={XClose}
              iconContainerProps={{
                width: 'unset',
                height: 'unset',
                bg: 'unset',
              }}
              iconProps={{
                width: '18px',
                height: '18px',
                color: primaryColor ? primaryColor : 'ats-primary',
              }}
              gap="4px"
              onClick={handleDelete}
              type="button"
              textProps={{ color: primaryColor ? primaryColor : 'ats-primary' }}
            />
          )}
        </Flex>
      </Flex>
      {isOpen && (
        <PreviewPdfModal isOpen={isOpen} onClose={onClose} file={file} />
      )}
    </>
  );
};
